/**
 * Centralized project configuration
 * Maps project URLs to their metadata: title, icon, color, category
 */

import {
	CpuChipIcon,
	MagnifyingGlassIcon,
	PresentationChartLineIcon,
	ServerStackIcon,
	SparklesIcon,
	CurrencyDollarIcon,
	TableCellsIcon,
	ChartBarIcon,
	DocumentTextIcon,
	FlagIcon,
	AcademicCapIcon,
	BanknotesIcon,
	QueueListIcon,
	LightBulbIcon,
	CalendarIcon,
	BeakerIcon,
	Squares2X2Icon,
	CubeTransparentIcon,
	StarIcon,
	MapPinIcon
} from '@heroicons/react/24/outline';
import { PoolIcon } from '../components/Icons';

/**
 * Category to color mapping
 * Defines which color each category uses
 */
export const categoryColors = {
	'analytics-engineering': 'bg-badger-green',
	'data-pipelines': 'bg-badger-blue',
	'simulations': 'bg-badger-purple',
	'games': 'bg-badger-purple', // Game simulations
	'betting': 'bg-badger-yellow',
	'pool': 'bg-badger-red', // Real-world data collection and ranking systems
	'other': 'bg-badger-orange' // For projects that don't fit main categories
};

/**
 * Get color class for a category
 * @param {string} category - Category name
 * @returns {string} - Color class
 */
export const getColorForCategory = (category) => {
	return categoryColors[category] || categoryColors['other'];
};

/**
 * All projects configuration
 * Each project has: path, title, icon, category, isCustomIcon (optional), backLink (optional)
 * 
 * backLink: Optional override for parent route navigation. If not provided, parent is auto-detected from hierarchy.
 *           Set to null to disable back link, or provide a string path to override default parent.
 */
export const allProjects = {
	// Analytics Engineering
	'/duckdb': {
		title: 'DuckDB Analytics Monitoring',
		icon: ServerStackIcon,
		category: 'analytics-engineering',
		isCustomIcon: false
	},
	'/analytics/designing-analytics-schemas': {
		title: 'Designing Analytics Schemas',
		icon: TableCellsIcon,
		category: 'analytics-engineering',
		isCustomIcon: false
	},
	
	// Data Pipelines
	'/cricket': {
		title: 'Explore Test Match Batting Careers',
		icon: MagnifyingGlassIcon,
		category: 'data-pipelines',
		isCustomIcon: false
	},
	'/data-visualisation/dynamic-d3-components': {
		title: 'Dynamic D3.js Graph',
		icon: SparklesIcon,
		category: 'data-pipelines',
		isCustomIcon: false
	},
	'/intersecting-lines': {
		title: 'Intersecting Lines',
		icon: SparklesIcon,
		category: 'data-pipelines',
		isCustomIcon: false
	},
	
	// Simulations
	'/sports-models': {
		title: 'Predictive Sports Modelling',
		icon: PresentationChartLineIcon,
		category: 'simulations',
		isCustomIcon: false
	},
	'/sports-models/english-pool-monte-carlo-simulation': {
		title: 'English Pool Monte Carlo Simulation',
		icon: CpuChipIcon,
		category: 'simulations',
		isCustomIcon: false
	},
	'/sports-models/sports-ranking-methods-colley-massey-elo': {
		title: 'Sports Ranking Methods: Colley, Massey, and ELO Ratings',
		icon: ChartBarIcon,
		category: 'simulations',
		isCustomIcon: false
	},
	'/sports-models/monte-carlo-simulation-sports-prediction': {
		title: 'Monte Carlo Simulations and Sports Predictions',
		icon: DocumentTextIcon,
		category: 'simulations',
		isCustomIcon: false
	},
	
	// Betting
	'/betting': {
		title: 'Sports Betting',
		icon: CurrencyDollarIcon,
		category: 'betting',
		isCustomIcon: false
	},
	'/betting/understand-sports-betting': {
		title: 'A Guide to Understanding Sports Betting',
		icon: AcademicCapIcon,
		category: 'betting',
		isCustomIcon: false
	},
	'/betting/sports-betting-bankroll-management-strategies': {
		title: 'Sports Betting Bankroll Management Strategies',
		icon: QueueListIcon,
		category: 'betting',
		isCustomIcon: false
	},
	'/betting/how-to-find-value-betting-on-sports': {
		title: 'Find Value Betting On Sports',
		icon: BanknotesIcon,
		category: 'betting',
		isCustomIcon: false
	},
	'/betting/how-to-maximise-sports-betting-accumulators': {
		title: 'Maximise Betting Accumulators',
		icon: LightBulbIcon,
		category: 'betting',
		isCustomIcon: false
	},
	'/betting/kelly-criterion-bankroll-management': {
		title: 'Kelly Criterion Bankroll Management',
		icon: QueueListIcon,
		category: 'betting',
		isCustomIcon: false
	},
	
	// Pool
	'/pool/matches': {
		title: 'Amateur Pool Rankings',
		icon: PoolIcon,
		category: 'pool',
		isCustomIcon: true
	},
	
	// Simulations - Sports Models
	'/sports-models/league-schedule-generator': {
		title: 'League Schedule Generator',
		icon: CalendarIcon,
		category: 'simulations',
		isCustomIcon: false
	},
	'/sports-models/english-pool-monte-carlo-simulation/shot-accuracy': {
		title: 'Stage 1: Shot Accuracy',
		icon: CpuChipIcon,
		category: 'simulations',
		isCustomIcon: false
	},
	'/sports-models/english-pool-monte-carlo-simulation/break-efficiency': {
		title: 'Stage 2: Break Efficiency',
		icon: CpuChipIcon,
		category: 'simulations',
		isCustomIcon: false
	},
	'/sports-models/english-pool-monte-carlo-simulation/foul-impact': {
		title: 'Stage 3: Foul Impact',
		icon: CpuChipIcon,
		category: 'simulations',
		isCustomIcon: false
	},
	'/sports-models/english-pool-monte-carlo-simulation/player-model': {
		title: 'Modelling a Player for Sports Simulations',
		icon: CpuChipIcon,
		category: 'simulations',
		isCustomIcon: false
	},
	'/sports-models/english-pool-monte-carlo-simulation/league-predictions': {
		title: 'League Prediction of Random Players',
		icon: CpuChipIcon,
		category: 'simulations',
		isCustomIcon: false
	},
	'/sports-models/english-pool-monte-carlo-simulation/league-predictions/custom': {
		title: 'Custom League Simulation',
		icon: CpuChipIcon,
		category: 'simulations',
		isCustomIcon: false
	},
	'/sports-models/english-pool-monte-carlo-simulation/glossary': {
		title: 'English Pool Monte Carlo Simulation Glossary',
		icon: CpuChipIcon,
		category: 'simulations',
		isCustomIcon: false
	},
	'/sports-models/golf': {
		title: 'Modelling Golf',
		icon: FlagIcon,
		category: 'simulations',
		isCustomIcon: false
	},
	'/sports-models/golf/golfer-data-model': {
		title: 'The Golfer Data Model',
		icon: CubeTransparentIcon,
		category: 'simulations',
		isCustomIcon: false
	},
	'/sports-models/golf/round-prediction': {
		title: 'Round Simulation',
		icon: FlagIcon,
		category: 'simulations',
		isCustomIcon: false
	},
	'/sports-models/golf/methodology': {
		title: 'Methodology',
		icon: AcademicCapIcon,
		category: 'simulations',
		isCustomIcon: false
	},
	'/sports-models/golf/profiles': {
		title: 'Tournament Simulation',
		icon: StarIcon,
		category: 'simulations',
		isCustomIcon: false
	},
	'/sports-models/golf/shot-level-simulation': {
		title: 'Shot-Level Simulation',
		icon: MapPinIcon,
		category: 'simulations',
		isCustomIcon: false
	},

	// Data Pipelines - Additional
	'/steam/games': {
		title: 'Steam Library',
		icon: SparklesIcon,
		category: 'data-pipelines',
		isCustomIcon: false
	},
	'/steam/insights': {
		title: 'Steam Insights',
		icon: PresentationChartLineIcon,
		category: 'data-pipelines',
		isCustomIcon: false
	},
	
	// Development/Testing (other category)
	'/playground': {
		title: 'Playground',
		icon: BeakerIcon,
		category: 'other',
		isCustomIcon: false
	},

	// Games
	'/games': {
		title: 'Games',
		icon: Squares2X2Icon,
		category: 'games',
		isCustomIcon: false
	},
	'/games/snakes-and-ladders': {
		title: 'Snakes and Ladders Simulation',
		icon: Squares2X2Icon,
		category: 'games',
		isCustomIcon: false
	},
	'/games/nonogram-solver': {
		title: 'Nonogram Row Solver',
		icon: Squares2X2Icon,
		category: 'games',
		isCustomIcon: false
	}
};

/**
 * Get project configuration by path
 * @param {string} path - Project path (e.g., '/betting/understand-sports-betting')
 * @returns {object|null} - Project config or null if not found
 */
export const getProjectConfig = (path) => {
	// Normalize path (ensure it starts with /)
	const normalizedPath = path.startsWith('/') ? path : `/${path}`;
	return allProjects[normalizedPath] || null;
};

/**
 * Get color class for a project path
 * @param {string} path - Project path
 * @returns {string} - Color class
 */
export const getColorForProject = (path) => {
	const project = getProjectConfig(path);
	if (!project) return null;
	return getColorForCategory(project.category);
};

/**
 * Get projects by category
 * @param {string} category - Category name
 * @returns {array} - Array of project configs with paths
 */
export const getProjectsByCategory = (category) => {
	return Object.entries(allProjects)
		.filter(([path, config]) => config.category === category)
		.map(([path, config]) => ({
			path,
			...config
		}));
};

/**
 * Featured projects for homepage
 * Returns array of project configs with paths
 */
export const getFeaturedProjects = () => {
	const featuredPaths = [
		'/duckdb',
		'/analytics/designing-analytics-schemas',
		'/cricket',
		'/data-visualisation/dynamic-d3-components',
		'/sports-models',
		'/sports-models/english-pool-monte-carlo-simulation',
		'/betting',
		'/pool/matches'
	];
	
	return featuredPaths.map(path => {
		const config = getProjectConfig(path);
		if (!config) return null;
		return {
			to: path, // Keep absolute paths - ProjectLink handles both relative and absolute
			title: config.title,
			icon: config.icon,
			colorClass: 'bg-badger-purple',
			iconColor: 'text-badger-purple',
			isCustomIcon: config.isCustomIcon || false
		};
	}).filter(Boolean);
};

/**
 * Project hierarchy configuration
 * Defines parent-child relationships and dynamic route patterns
 */
export const projectHierarchy = {
	// Analytics Engineering
	'/analytics': {
		category: 'analytics-engineering',
		children: {
			'/analytics/designing-analytics-schemas': { static: true }
		}
	},
	
	// Data Pipelines
	'/cricket': {
		category: 'data-pipelines',
		children: {
			'/cricket/player/:playerName': {
				dynamic: true,
				pattern: /^\/cricket\/player\/.+$/,
				category: 'data-pipelines'
			}
		}
	},
	'/data-visualisation': {
		category: 'data-pipelines',
		children: {
			'/data-visualisation/dynamic-d3-components': { static: true },
			'/intersecting-lines': { static: true }
		}
	},
	'/steam': {
		category: 'data-pipelines',
		children: {
			'/steam/games': { static: true },
			'/steam/insights': { static: true }
		}
	},
	
	// Simulations
	'/sports-models': {
		category: 'simulations',
		children: {
			'/sports-models/english-pool-monte-carlo-simulation': {
				static: true,
				children: {
					'/sports-models/english-pool-monte-carlo-simulation/shot-accuracy': { static: true },
					'/sports-models/english-pool-monte-carlo-simulation/break-efficiency': { static: true },
					'/sports-models/english-pool-monte-carlo-simulation/foul-impact': { static: true },
					'/sports-models/english-pool-monte-carlo-simulation/player-model': { static: true },
					'/sports-models/english-pool-monte-carlo-simulation/league-predictions': {
						static: true,
						children: {
							'/sports-models/english-pool-monte-carlo-simulation/league-predictions/custom': { static: true }
						}
					},
					'/sports-models/english-pool-monte-carlo-simulation/glossary': { static: true }
				}
			},
			'/sports-models/league-schedule-generator': { static: true },
			'/sports-models/sports-ranking-methods-colley-massey-elo': { static: true },
			'/sports-models/monte-carlo-simulation-sports-prediction': { static: true },
			'/sports-models/golf': {
				static: true,
				children: {
					'/sports-models/golf/golfer-data-model': { static: true },
					'/sports-models/golf/round-prediction': { static: true },
					'/sports-models/golf/profiles': { static: true },
					'/sports-models/golf/shot-level-simulation': { static: true },
					'/sports-models/golf/methodology': { static: true }
				}
			}
		}
	},
	
	// Betting
	'/betting': {
		category: 'betting',
		children: {
			'/betting/understand-sports-betting': { static: true },
			'/betting/how-to-find-value-betting-on-sports': { static: true },
			'/betting/how-to-maximise-sports-betting-accumulators': { static: true },
			'/betting/sports-betting-bankroll-management-strategies': { static: true },
			'/betting/kelly-criterion-bankroll-management': { static: true }
		}
	},
	
	// Games
	'/games': {
		category: 'games',
		children: {
			'/games/snakes-and-ladders': { static: true },
			'/games/nonogram-solver': { static: true }
		}
	},

	// Pool (Real-world data analytics)
	// /pool/matches is the top-level pool landing page (no parent)
	'/pool/matches': {
		category: 'pool',
		children: {
			'/pool/player/:playerID': { 
				dynamic: true,
				pattern: /^\/pool\/player\/\d+$/,
				category: 'pool',
				// Title and other metadata come from API
			},
			'/pool/group/:groupUrlSlug': {
				dynamic: true,
				pattern: /^\/pool\/group\/[^/]+$/,
				category: 'pool',
				children: {
					'/pool/group/:groupUrlSlug/ratings-explanation': {
						dynamic: true,
						pattern: /^\/pool\/group\/[^/]+\/ratings-explanation$/,
						category: 'pool'
					}
				}
			},
			'/pool/predictions/:groupUrlSlug': {
				dynamic: true,
				pattern: /^\/pool\/predictions\/[^/]+$/,
				category: 'pool'
			}
		}
	}
};

/**
 * Check if a path matches a dynamic route pattern
 * @param {string} path - Path to check
 * @param {object} hierarchyNode - Hierarchy node with pattern
 * @returns {boolean} - Whether path matches
 */
const matchesDynamicPattern = (path, hierarchyNode) => {
	if (!hierarchyNode.pattern) return false;
	return hierarchyNode.pattern.test(path);
};

/**
 * Get category for a path using hierarchy
 * Ensures consistency with theme config
 * @param {string} path - Path to check
 * @returns {string|null} - Category name or null
 */
export const getCategoryForPath = (path) => {
	const normalizedPath = path.startsWith('/') ? path : `/${path}`;
	
	// Check static projects first
	const staticProject = getProjectConfig(normalizedPath);
	if (staticProject) {
		return staticProject.category;
	}
	
	// Check hierarchy for dynamic routes
	const checkHierarchy = (node, currentPath) => {
		if (node.category && matchesDynamicPattern(currentPath, node)) {
			return node.category;
		}
		
		if (node.children) {
			for (const [childPath, childNode] of Object.entries(node.children)) {
				if (childNode.dynamic && matchesDynamicPattern(currentPath, childNode)) {
					return childNode.category || node.category;
				}
				if (childNode.static && currentPath.startsWith(childPath)) {
					return childNode.category || node.category;
				}
				// Recursively check nested children
				if (childNode.children) {
					const nestedResult = checkHierarchy(childNode, currentPath);
					if (nestedResult) return nestedResult;
				}
			}
		}
		
		return null;
	};
	
	for (const [rootPath, rootNode] of Object.entries(projectHierarchy)) {
		if (normalizedPath.startsWith(rootPath)) {
			const category = checkHierarchy(rootNode, normalizedPath);
			if (category) return category;
			return rootNode.category;
		}
	}
	
	return null;
};


/**
 * Get projects for a specific section (e.g., betting, sports-models)
 * Supports both static and dynamic routes
 * @param {string} sectionPath - Section path (e.g., '/betting', '/sports-models', '/pool')
 * @param {object} options - Options for filtering
 * @param {boolean} options.includeDynamic - Include dynamic route patterns (default: false)
 * @param {function} options.dynamicProjectMapper - Function to map dynamic projects (e.g., from API data)
 * @returns {array} - Array of project configs with paths
 */
export const getProjectsForSection = (sectionPath, options = {}) => {
	const { includeDynamic = false, dynamicProjectMapper = null } = options;
	const normalizedPath = sectionPath.startsWith('/') ? sectionPath : `/${sectionPath}`;
	
	const projects = [];
	const addedPaths = new Set(); // Track added paths to avoid duplicates
	
	// Check hierarchy first to get only direct children
	if (normalizedPath in projectHierarchy) {
		const hierarchyNode = projectHierarchy[normalizedPath];
		
		if (hierarchyNode.children) {
			Object.entries(hierarchyNode.children).forEach(([childPath, childNode]) => {
				// Skip if already added
				if (addedPaths.has(childPath)) return;
				
				if (childNode.dynamic) {
					// Handle dynamic projects only if includeDynamic is true and mapper is provided
					if (includeDynamic && dynamicProjectMapper) {
						const dynamicProjects = dynamicProjectMapper(childPath, childNode);
						dynamicProjects.forEach(project => {
							if (!addedPaths.has(project.to)) {
								addedPaths.add(project.to);
								projects.push(project);
							}
						});
					}
				} else if (childNode.static) {
					// Add static child projects from hierarchy (direct children only)
					const childConfig = getProjectConfig(childPath);
					if (childConfig) {
						addedPaths.add(childPath);
						projects.push({
							to: childPath,
							title: childConfig.title,
							icon: childConfig.icon,
							colorClass: getColorForCategory(childConfig.category),
							isCustomIcon: childConfig.isCustomIcon || false
						});
					}
				}
			});
		}
	} else {
		// Fallback: if no hierarchy entry exists, get direct children from allProjects
		// This handles cases where projects exist but aren't in hierarchy
		// Only include paths that are direct children (one level deeper)
		const pathSegments = normalizedPath.split('/').filter(Boolean);
		const directChildren = Object.entries(allProjects)
			.filter(([path, config]) => {
				const childSegments = path.split('/').filter(Boolean);
				// Must be exactly one level deeper
				if (childSegments.length !== pathSegments.length + 1) {
					return false;
				}
				// Check if path starts with normalizedPath
				return path.startsWith(normalizedPath) && path !== normalizedPath;
			})
			.map(([path, config]) => {
				if (addedPaths.has(path)) return null;
				addedPaths.add(path);
				return {
					to: path,
					title: config.title,
					icon: config.icon,
					colorClass: getColorForCategory(config.category),
					isCustomIcon: config.isCustomIcon || false
				};
			})
			.filter(Boolean);
		
		projects.push(...directChildren);
	}
	
	return projects;
};

/**
 * Get parent route for navigation (backLink)
 * Automatically determines parent from hierarchy, but can be overridden in project config
 * @param {string} pathname - Current pathname (e.g., '/betting/understand-sports-betting')
 * @returns {string|null} - Parent route path or null if no parent
 */
export const getParentRoute = (pathname) => {
	const normalizedPath = pathname === '/' ? '/' : pathname.replace(/\/$/, '');
	
	// Check if project config has explicit backLink override
	const projectConfig = getProjectConfig(normalizedPath);
	if (projectConfig && projectConfig.backLink !== undefined) {
		return projectConfig.backLink;
	}
	
	// Special case: /pool/matches is the top-level pool landing page (no back link)
	if (normalizedPath === '/pool/matches') {
		return null;
	}
	
	// Check hierarchy for immediate parent
	const parent = getParentProject(normalizedPath);
	if (parent) {
		return parent.path;
	}
	
	// Fallback: remove last segment for dynamic routes or simple paths
	const segments = normalizedPath.split('/').filter(Boolean);
	if (segments.length > 1) {
		// For dynamic routes like /pool/player/123, go back to /pool/matches
		// For static routes, just remove last segment
		const parentPath = '/' + segments.slice(0, -1).join('/');
		
		// Special cases: some routes should go to specific parents
		if (normalizedPath.startsWith('/pool/player/') || 
		    normalizedPath.startsWith('/pool/group/') ||
		    normalizedPath.startsWith('/pool/predictions/')) {
			return '/pool/matches';
		}
		
		if (normalizedPath.startsWith('/cricket/player/')) {
			return '/cricket';
		}
		
		return parentPath;
	}
	
	// Top-level routes: no back link (or could default to '/')
	return null;
};

/**
 * Get parent project for a given path (useful for breadcrumbs, back links)
 * @param {string} path - Current path
 * @returns {object|null} - Parent project config or null
 */
export const getParentProject = (path) => {
	const normalizedPath = path.startsWith('/') ? path : `/${path}`;
	
	// Find parent in hierarchy
	for (const [rootPath, rootNode] of Object.entries(projectHierarchy)) {
		if (normalizedPath.startsWith(rootPath) && normalizedPath !== rootPath) {
			// Check if this is a direct child
			if (rootNode.children) {
				for (const [childPath] of Object.entries(rootNode.children)) {
					if (normalizedPath.startsWith(childPath) && normalizedPath !== childPath) {
						// This is a nested child, return the immediate parent
						const parentConfig = getProjectConfig(childPath);
						if (parentConfig) {
							return {
								path: childPath,
								...parentConfig,
								colorClass: getColorForCategory(parentConfig.category)
							};
						}
					}
				}
			}
			
			// Return root as parent
			const rootConfig = getProjectConfig(rootPath);
			if (rootConfig) {
				return {
					path: rootPath,
					...rootConfig,
					colorClass: getColorForCategory(rootConfig.category)
				};
			}
		}
	}
	
	return null;
};

